// SYSTEM_ROUTE: /business
import { useState } from 'react';
import type { Venture, VentureStatus } from '@life-os/app-core';
import { VENTURE_STATUSES, formatMinor, summarizeVentures } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STATUS_TONE: Record<VentureStatus, ChipTone> = {
  idea: 'muted',
  experiment: 'warn',
  active: 'primary',
  paused: 'neutral',
  closed: 'muted',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

export function BusinessScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [sector, setSector] = useState('');

  const ventures = useAsync<readonly Venture[]>(() => services.business.list(), [revision]);
  const list = ventures.value ?? [];
  const summary = summarizeVentures(list);

  const add = async () => {
    if (title.trim().length === 0) return;
    await services.business.create({ title, sector });
    setTitle('');
    setSector('');
    refresh();
  };

  const setRevenue = async (v: Venture, major: string) => {
    const minor = Math.max(0, Math.round((Number(major) || 0) * 100));
    if (minor === v.monthlyRevenueMinor) return;
    await services.business.update(v.id, { monthlyRevenueMinor: minor });
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Business & Ventures</>} subtitle={<>Owner-level portfolio of ideas, experiments, and ventures. Strategy and summaries live here;
            detailed operations and the money ledger live elsewhere.</>} actions={<><Chip tone="primary">{formatMinor(summary.activeMonthlyRevenueMinor)} / mo active</Chip></>} />

      <Pane title="Portfolio" hint={`${summary.live} live of ${summary.total}`}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {VENTURE_STATUSES.map((st) => (
            <Chip key={st} tone={summary.byStatus[st] > 0 ? STATUS_TONE[st] : 'muted'}>
              {st} {summary.byStatus[st] > 0 ? summary.byStatus[st] : ''}
            </Chip>
          ))}
        </div>
      </Pane>

      <Pane title="New venture">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={title}
            placeholder="Venture / idea…"
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Venture title"
          />
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 140px' }}
            value={sector}
            placeholder="Sector"
            onChange={(e) => setSector(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="Sector"
          />
          <Button variant="primary" onClick={() => void add()}>
            Add
          </Button>
        </div>
      </Pane>

      <Pane title="Ventures" hint={`${list.length}`}>
        {list.length === 0 ? (
          <EmptyState title="No ventures yet">
            Capture an idea. Move it idea → experiment → active as it earns its place. Promote real ones to
            a dedicated Company OS later.
          </EmptyState>
        ) : (
          <Stack>
            {list.map((v) => (
              <div key={String(v.id)} className="lc-row" style={{ alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <strong>{v.title}</strong>
                  {v.sector ? <span style={{ color: 'var(--lc-on-surface-variant)' }}> · {v.sector}</span> : null}
                </div>
                {v.status === 'active' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--lc-text-sm)', color: 'var(--lc-on-surface-variant)' }}>
                    rev/mo
                    <input
                      type="number"
                      min={0}
                      step="1"
                      defaultValue={(v.monthlyRevenueMinor / 100).toString()}
                      onBlur={(e) => void setRevenue(v, e.target.value)}
                      aria-label="Monthly revenue"
                      style={{ ...inputStyle, width: 90 }}
                    />
                  </label>
                ) : null}
                <Chip tone={STATUS_TONE[v.status]}>{v.status}</Chip>
                <select
                  value={v.status}
                  onChange={(e) => void services.business.setStatus(v.id, e.target.value as VentureStatus).then(refresh)}
                  aria-label="Venture status"
                  style={inputStyle}
                >
                  {VENTURE_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </Stack>
        )}
      </Pane>
    </>
  );
}
